import { useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  IconBillCheck,
  IconChart,
  IconEdit,
  IconIdCard,
  IconPill,
  IconPrinter,
  IconRefreshCw,
  IconStethoscope,
  IconWallet,
} from '../../components/layout/icons';
import { BillActionGrid, BillActionHoverTile, BillActionTile } from '../../components/ui/BillActionTile';
import { EmptyState, ErrorState } from '../../components/ui/Feedback';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { openAuthenticatedRoute } from '../../lib/auth';
import { doctorDisplayName } from '../../lib/doctor-name';
import { useOpdPatientBills, useOpdRegistration } from './opd.queries';
import { currency, formatOpdDate } from './opd.utils';
import { OpdAllBillList } from './OpdAllBillList';
import { OpdAllReceiptList } from './OpdAllReceiptList';
import { OpdAllRefundList } from './OpdAllRefundList';
import { OpdEditBillForm } from './OpdEditBillForm';
import { OpdNewReceiptForm } from './OpdNewReceiptForm';
import { OpdPrescriptionForm } from './OpdPrescriptionForm';

type BillTab = 'bill-details' | 'print-bill' | 'all-bill' | 'create-prescription' | 'prescription' | 'edit-bill' | 'new-receipt' | 'all-receipt' | 'refund' | 'all-refund';

type BlankTab = { key: Exclude<BillTab, 'bill-details'>; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; soon: boolean; message: string };

const tabsBeforeBillDetails: BlankTab[] = [
  { key: 'create-prescription', label: 'Create E-Prescription', icon: IconPill, soon: false, message: 'Prescription creation is not available for OPD bills yet.' },
  { key: 'prescription', label: 'Prescription', icon: IconStethoscope, soon: true, message: 'Prescription records are not available for OPD bills yet.' },
];

const tabsAfterBillDetails: BlankTab[] = [
  { key: 'edit-bill', label: 'Edit Bill', icon: IconEdit, soon: false, message: 'Editing an OPD bill is not available yet.' },
  { key: 'print-bill', label: 'Print Bill', icon: IconPrinter, soon: false, message: 'This section is not available here yet. Use the Print button at the top of the page to print this bill.' },
  { key: 'all-bill', label: 'All Bill', icon: IconWallet, soon: false, message: 'A consolidated bill list is not available here yet.' },
  { key: 'all-receipt', label: 'All Receipt', icon: IconChart, soon: false, message: 'A receipts list is not available yet.' },
  { key: 'all-refund', label: 'All Refund', icon: IconRefreshCw, soon: false, message: 'A refunds list is not available yet.' },
];

const blankTabs: BlankTab[] = [...tabsBeforeBillDetails, ...tabsAfterBillDetails];
const upper = (value: string | number | null | undefined) => String(value ?? '-').toUpperCase();

export const OpdRegistrationDetailPage = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const tabParam = params.get('tab');
  const editPrescription = params.get('mode') === 'edit';
  const [activeTab, setActiveTab] = useState<BillTab>(() => (tabParam === 'create-prescription' ? tabParam : 'bill-details'));
  useEffect(() => {
    if (tabParam === 'create-prescription') setActiveTab(tabParam);
  }, [registrationId, tabParam]);
  const query = useOpdRegistration(registrationId);
  const uhid = query.data?.patientSnapshot.uhid ?? null;
  const opdBillsQuery = useOpdPatientBills(uhid);
  useEffect(() => { if (query.data && params.get('print') === '1') window.setTimeout(() => window.print(), 250); }, [params, query.data]);
  if (query.isLoading) return <DetailSkeleton label="Loading registration details" />;
  if (query.isError || !query.data) return <ErrorState title="OPD registration not found" message="The registration could not be loaded." />;
  const row = query.data;
  const address = [row.patientSnapshot.address, row.patientSnapshot.district, row.patientSnapshot.state, row.patientSnapshot.pinCode].filter(Boolean).join(', ') || '-';
  const billStatusLabel = row.billing.billStatus === 'due' ? 'Due' : row.billing.billStatus === 'cancelled' ? 'Cancelled' : 'Paid';
  const activeBlankTab = blankTabs.find((tab) => tab.key === activeTab);
  const activeOpdBills = (opdBillsQuery.data?.data ?? []).filter((bill) => bill.billing.billStatus !== 'cancelled');
  const totalDue = activeOpdBills.reduce((sum, bill) => sum + bill.billing.dueAmount, 0);
  const totalCredit = activeOpdBills.reduce((sum, bill) => sum + bill.billing.creditAmount, 0);
  const printBill = () => openAuthenticatedRoute(`/opd/${row.id}/print-bill`);
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) void navigate(-1);
    else void navigate('/opd');
  };

  return (
    <div className="page-stack opd-page opd-bill-page">
      <div className="page-heading opd-print-hidden">
        <div><p className="eyebrow">OPD</p><h1>OPD Registration Details</h1></div>
        <div className="opd-heading-actions">
          <button className="button button-secondary" type="button" onClick={goBack}>Back</button>
        </div>
      </div>

      <div className="opd-bill-layout">
        <aside className="content-card opd-bill-sidebar">
          <div className="opd-bill-patient-box">
            <p><strong>PATIENT NAME : </strong>{upper(row.patientSnapshot.name)}</p>
            <p><strong>UHID : </strong>{upper(row.patientSnapshot.uhid)}</p>
            <p><strong>SEX : </strong>{upper(row.patientSnapshot.gender)} || <strong>AGE : </strong>{row.patientSnapshot.ageYears}Y {row.patientSnapshot.ageMonths}M {row.patientSnapshot.ageDays}D</p>
            <p><strong>MOBILE : </strong>{upper(row.patientSnapshot.phone)}</p>
            <p><strong>ADDRESS : </strong>{upper(address)}</p>
            <p><strong>DEPARTMENT : </strong>{upper(row.department.name)}</p>
            <p><strong>CONSULTANT DOCTOR : </strong>{upper(doctorDisplayName(row.doctor.name))}</p>
          </div>
          {totalCredit > 0 || totalDue > 0 ? (
            <div className="opd-bill-patient-balance">
              {totalCredit > 0 ? <p><strong>Credit Amount : </strong><span className="opd-bill-patient-credit">{currency(totalCredit)}</span></p> : null}
              {totalDue > 0 ? <p><strong>Overall Due : </strong><span className="opd-bill-patient-due">{currency(totalDue)}</span></p> : null}
            </div>
          ) : null}
          <BillActionGrid>
            <BillActionTile icon={IconIdCard} label="Patient Profile" to={`/patients/${row.patient.id}`} />
            {tabsBeforeBillDetails.map((tab) => (
              tab.key === 'prescription' ? (
                <BillActionHoverTile
                  key={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  items={[
                    { label: 'Prescription without letterhead', onClick: () => openAuthenticatedRoute(`/opd/${row.id}/print-prescription?header=0`) },
                    { label: 'Prescription with letterhead', onClick: () => openAuthenticatedRoute(`/opd/${row.id}/print-prescription?header=1`) },
                  ]}
                />
              ) : tab.key === 'create-prescription' ? (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key && !editPrescription} soon={tab.soon} onClick={() => {
                  setActiveTab(tab.key);
                  void navigate(`/opd/${row.id}?tab=create-prescription`);
                }} />
              ) : (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
            <BillActionTile icon={IconBillCheck} label="Bill Details" active={activeTab === 'bill-details'} onClick={() => setActiveTab('bill-details')} />
            {row.billing.dueAmount > 0 ? <BillActionTile icon={IconWallet} label="New Receipt" active={activeTab === 'new-receipt'} onClick={() => setActiveTab('new-receipt')} /> : null}
            {row.billing.creditAmount > 0 ? <BillActionTile icon={IconRefreshCw} label="Refund" active={activeTab === 'refund'} onClick={() => setActiveTab('refund')} /> : null}
            {tabsAfterBillDetails.map((tab) => (
              tab.key === 'print-bill' ? (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} onClick={printBill} />
              ) : (
                <BillActionTile key={tab.key} icon={tab.icon} label={tab.label} active={activeTab === tab.key} soon={tab.soon} onClick={() => setActiveTab(tab.key)} />
              )
            ))}
          </BillActionGrid>
        </aside>

        <section className="content-card opd-bill">
          {activeTab === 'create-prescription' ? (
            <OpdPrescriptionForm row={row} editMode={editPrescription} />
          ) : activeTab === 'edit-bill' ? (
            <OpdEditBillForm row={row} onCancelled={() => setActiveTab('bill-details')} />
          ) : activeTab === 'new-receipt' ? (
            <OpdNewReceiptForm row={row} onRecorded={() => setActiveTab('bill-details')} />
          ) : activeTab === 'all-bill' ? (
            <OpdAllBillList row={row} />
          ) : activeTab === 'all-receipt' ? (
            <OpdAllReceiptList row={row} />
          ) : activeTab === 'refund' || activeTab === 'all-refund' ? (
            <OpdAllRefundList row={row} showForm={activeTab === 'refund'} />
          ) : activeBlankTab ? (
            <EmptyState
              title={`${activeBlankTab.label}${activeBlankTab.soon ? ' — Coming soon' : ''}`}
              message={activeBlankTab.message}
              action={{ label: 'Back to Bill Details', onClick: () => setActiveTab('bill-details') }}
            />
          ) : (
          <>
          <header className="opd-bill-banner">
            <div className="opd-bill-banner-row">
              <div className="opd-bill-banner-fields">
                <span>Bill No : {row.billing.billNumber}</span>
                <span>Bill Date : {formatOpdDate(row.appointmentDate)}</span>
                <span>Bill Section : OPD</span>
              </div>
              <span className={`opd-badge ${row.billing.billStatus}`}>{billStatusLabel}</span>
            </div>
            <p>Bill Created By - {row.generatedBy.name}{row.editedBy ? ` || Last Edit By - ${row.editedBy.name}` : ''}</p>
          </header>

          <div className="table-scroll">
            <table className="opd-bill-charge-table">
              <thead><tr><th>#</th><th>Date</th><th>Charge Name</th><th>Charge Amount</th><th>Qty</th><th>Dis Amt</th><th>Dis %</th><th>Status</th><th>Total</th></tr></thead>
              <tbody>
                {row.charges.length === 0
                  ? <tr><td className="opd-bill-empty" colSpan={9}>No itemized charge rows were stored for this legacy bill.</td></tr>
                  : row.charges.map((line, index) => (
                    <tr key={line.charge} className={row.billing.billStatus === 'paid' ? 'paid' : undefined}>
                      <td>{index + 1}</td>
                      <td>{formatOpdDate(row.appointmentDate)}</td>
                      <td>{line.chargeName}</td>
                      <td>{currency(line.rate)}</td>
                      <td>{line.quantity}</td>
                      <td>{currency(line.discountAmount)}</td>
                      <td>{line.discountPercent}%</td>
                      <td><span className={`opd-badge ${row.billing.billStatus}`}>{billStatusLabel}</span></td>
                      <td>{currency(line.amount)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className="opd-bill-summary">
            <div><span>Total</span><strong>{currency(row.billing.total)}</strong></div>
            <div><span>Discount</span><strong>{currency(row.billing.totalDiscount)}</strong></div>
            <div><span>Payable Amount</span><strong>{currency(row.billing.grandTotal)}</strong></div>
            <div><span>Total Payment</span><strong>{currency(row.billing.totalPayment)}</strong></div>
            {row.billing.totalRefunded > 0 ? <div className="refund"><span>Total Refund</span><strong>{currency(row.billing.totalRefunded)}</strong></div> : null}
            {row.billing.dueAmount > 0 ? <div className="due"><span>Due</span><strong>{currency(row.billing.dueAmount)}</strong></div> : null}
            {row.billing.creditAmount > 0 ? <div className="credit"><span>Credit</span><strong>{currency(row.billing.creditAmount)}</strong></div> : null}
          </div>

          <section className="opd-bill-subsection">
            <div className="opd-bill-subsection-head">
              <h3>Payment / Receipt List</h3>
              <span>Total Payment <strong>{currency(row.billing.totalPayment)}</strong></span>
            </div>
            {row.payments.length === 0 ? (
              <p className="opd-bill-empty-note">No payments recorded for this bill.</p>
            ) : (
              <div className="table-scroll">
                <table className="opd-bill-payment-table">
                  <thead><tr><th>#</th><th>Date</th><th>Mode</th><th>Bank</th><th>Amount</th></tr></thead>
                  <tbody>
                    {row.payments.map((payment, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{formatOpdDate(row.appointmentDate)}</td>
                        <td>{payment.mode}</td>
                        <td>{payment.bankName ?? '-'}</td>
                        <td>{currency(payment.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="opd-bill-subsection">
            <h3>Referral Details</h3>
            <dl className="opd-bill-referral">
              <div><dt>Market By</dt><dd>{row.marketBy?.name ?? '-'}</dd></div>
              <div><dt>Provider</dt><dd>{row.provider?.name ?? '-'}</dd></div>
              <div><dt>Referred By</dt><dd>{row.referredBy?.name ?? '-'}</dd></div>
            </dl>
          </section>
          </>
          )}
        </section>
      </div>
    </div>
  );
};
